import { inject, ResourceRef, resource } from '@angular/core';
import { ErrorHandlingService } from '@errors/error-handling.service';

/** Options for `createPageResource`. */
export interface PageResourceOptions<TValue, TParams> {
  /** Reactive params; return `undefined` to idle the resource. Omit for resources that load once immediately. */
  params?: () => TParams | undefined;
  /** Loads the value for the given params. */
  loadAsync: (params: TParams) => Promise<TValue>;
  /** Produces the (translated) toast message shown when loading fails. Omit to fail silently (still logged via `ErrorHandlingService`, no toast shown). */
  errorMessage?: () => string;
  onError?: () => void;
  /**
   * Value the resource holds while idle/loading AND resolves to when loading
   * fails (the error itself is reported via `ErrorHandlingService`). Because it
   * doubles as the resource's `defaultValue`, `value()` is never `undefined`.
   */
  fallbackValue: TValue;
}

/**
 * Creates a page `resource()` with the standard load-error workflow:
 * failures are routed to `ErrorHandlingService` (toast + logging) and the
 * resource resolves to `fallbackValue` so the page can still render.
 */
export function createPageResource<TValue, TParams = unknown>(options: PageResourceOptions<TValue, TParams>): ResourceRef<TValue> {
  const errorHandler = inject(ErrorHandlingService);

  return resource({
    params: options.params as () => TParams | undefined,
    loader: async ({ params }): Promise<TValue> => {
      try {
        return await options.loadAsync(params);
      } catch (error: unknown) {
        void errorHandler.handleErrorAsync(error, options.errorMessage?.());
        options.onError?.();
        return options.fallbackValue;
      }
    },
    defaultValue: options.fallbackValue
  });
}
